import { useCallback, useEffect, useRef } from 'react';

/**
 * Runs an async mutation at most once at a time. While a call is in flight,
 * subsequent invocations are coalesced — only the latest argument is kept,
 * and a single follow-up call is fired when the in-flight call settles.
 *
 * This is useful for inputs that fire rapid updates (e.g. tabbing through
 * fields) where the server only cares about the latest value and concurrent
 * writes would otherwise produce conflicts.
 */
export function useSerializedMutation<TArg>(
  mutationFn: (arg: TArg) => Promise<unknown>,
) {
  const mutationFnRef = useRef(mutationFn);
  mutationFnRef.current = mutationFn;

  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const pendingArgRef = useRef<{ arg: TArg } | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const run = useCallback((arg: TArg): Promise<void> => {
    if (inFlightRef.current) {
      pendingArgRef.current = { arg };
      return inFlightRef.current.then(() => undefined).catch(() => undefined);
    }

    const execute = async (initialArg: TArg) => {
      let currentArg = initialArg;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          await mutationFnRef.current(currentArg);
        } catch {
          // Swallow — mutationFn is expected to surface its own errors.
        }
        if (!isMountedRef.current || !pendingArgRef.current) {
          return;
        }
        currentArg = pendingArgRef.current.arg;
        pendingArgRef.current = null;
      }
    };

    const promise = execute(arg).finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, []);

  return run;
}
